import styled from 'styled-components'
import { Container, ContainerHeader } from 'components/Containers/Containers'
import { tokens } from 'styling/tokens'
import { Head1 } from 'styling/Typography'

export const TodayTitle = styled(Head1)`
  font-size: 1.5rem;
  line-height: 1.3;
  color: ${tokens.text.primary};
`

export const SwitcherRow = styled.div`
  margin-top: 0.75rem;
  max-width: 100%;
  overflow-x: auto;
`

export const EnvironRow = styled.div`
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`

export const EnvironSegment = styled.div<{ $tone: 'warning' | 'danger' | 'neutral' }>`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 16rem;
  padding: 0.375rem 0.75rem;
  border-radius: ${tokens.radius.chip};
  border: 1px solid ${tokens.border.hairline};
  background: ${({ $tone }) => tokens.status[$tone].bg};
  font-size: ${tokens.font.label};
  line-height: 1.5;
  color: ${({ $tone }) => ($tone === 'neutral' ? tokens.text.muted : tokens.status[$tone].fg)};
`

export const QuestionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 1rem 0;
  @media (max-width: 70rem) {
    grid-template-columns: 1fr;
  }
`

export const QuestionCardWrap = styled.div<{ $tone: 'success' | 'warning' | 'danger' }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: ${tokens.radius.card};
  border: 1px solid ${tokens.border.hairline};
  border-left: 4px solid ${({ $tone }) => tokens.status[$tone].fg};
  background: ${({ $tone }) => tokens.status[$tone].bg};
  box-shadow: ${tokens.shadow.card};
`

export const QuestionLabel = styled.span`
  font-size: ${tokens.font.micro};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.text.muted};
`

export const AnswerSentence = styled.p<{ $tone: 'success' | 'warning' | 'danger' }>`
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ $tone }) => tokens.status[$tone].fg};
`

export const AnswerCaption = styled.span`
  font-size: ${tokens.font.label};
  color: ${tokens.text.secondary};
`

export const TrendRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
`

export const TrendChip = styled.span`
  padding: 0.125rem 0.375rem;
  border-radius: ${tokens.radius.chip};
  border: 1px solid ${tokens.border.hairline};
  background: ${tokens.surface.inset};
  font-size: ${tokens.font.micro};
  line-height: 1.4;
  color: ${tokens.text.muted};
`

export const TrendNote = styled.span`
  margin-left: 0.25rem;
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
`

export const ViewLink = styled.button`
  align-self: flex-start;
  margin-top: 0.25rem;
  padding: 0;
  border: none;
  background: transparent;
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
  cursor: pointer;
  transition: color ${tokens.motion.duration} ${tokens.motion.ease};
  &:hover {
    color: ${tokens.accent.onDark};
  }
`

export const InboxList = styled.div`
  display: flex;
  flex-direction: column;
`

export const InboxRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem 0;
  & + & {
    border-top: 1px solid ${tokens.border.hairline};
  }
  &:first-child {
    padding-top: 0.25rem;
  }
  &:last-child {
    padding-bottom: 0.25rem;
  }
`

export const InboxDot = styled.span<{ $tone: 'danger' | 'warning' | 'info' }>`
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  margin-top: 0.375rem;
  border-radius: 999px;
  background: ${({ $tone }) => tokens.status[$tone].fg};
  box-shadow: 0 0 0 4px ${({ $tone }) => tokens.status[$tone].bg};
`

export const InboxTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 16rem;
`

export const InboxSentence = styled.p`
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${tokens.text.primary};
`

export const InboxSource = styled.span`
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
`

export const InboxActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  margin-left: auto;
`

export const InboxChevron = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: ${tokens.radius.chip};
  background: transparent;
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
  cursor: pointer;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform ${tokens.motion.duration} ${tokens.motion.ease},
    color ${tokens.motion.duration} ${tokens.motion.ease};
  &:hover {
    color: ${tokens.text.primary};
  }
`

export const InboxDetailBlock = styled.div`
  flex-basis: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-left: 1.25rem;
  padding: 0.75rem 1rem;
  border-radius: ${tokens.radius.control};
  border: 1px solid ${tokens.border.hairline};
  background: ${tokens.surface.inset};
`

export const InboxDetailLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
`

export const InboxDetailLabel = styled.span`
  flex-shrink: 0;
  width: 6.5rem;
  font-size: ${tokens.font.micro};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.text.muted};
`

export const InboxDetailValue = styled.span`
  font-size: ${tokens.font.body};
  line-height: 1.5;
  color: ${tokens.text.secondary};
`

export const InboxDetailActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
`

export const HandledSection = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${tokens.border.hairline};
`

export const HandledToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  border: none;
  background: transparent;
  font-size: ${tokens.font.label};
  font-weight: 600;
  color: ${tokens.text.muted};
  cursor: pointer;
  transition: color ${tokens.motion.duration} ${tokens.motion.ease};
  &:hover {
    color: ${tokens.text.primary};
  }
`

export const HandledGlyph = styled.span<{ $open: boolean }>`
  display: inline-block;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform ${tokens.motion.duration} ${tokens.motion.ease};
`

export const HandledList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.25rem;
`

export const HandledRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  & + & {
    border-top: 1px solid ${tokens.border.hairline};
  }
`

export const HandledMark = styled.span<{ $outcome: 'approved' | 'dismissed' | 'acknowledged' }>`
  flex-shrink: 0;
  font-size: ${tokens.font.label};
  color: ${({ $outcome }) => ($outcome === 'approved' ? tokens.status.success.fg : tokens.text.muted)};
`

export const HandledText = styled.span`
  flex: 1;
  font-size: ${tokens.font.label};
  line-height: 1.5;
  color: ${tokens.text.muted};
`

export const EmptyInboxWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 1.5rem 1rem;
  text-align: center;
`

export const EmptyInboxTitle = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: ${tokens.status.success.fg};
`

export const FooterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`

export const FooterChip = styled.button`
  padding: 0.375rem 0.75rem;
  border-radius: ${tokens.radius.chip};
  border: 1px solid ${tokens.border.hairline};
  background: ${tokens.surface.inset};
  font-size: ${tokens.font.label};
  color: ${tokens.text.secondary};
  cursor: pointer;
  transition: border-color ${tokens.motion.duration} ${tokens.motion.ease},
    color ${tokens.motion.duration} ${tokens.motion.ease};
  &:hover {
    border-color: ${tokens.accent.glowBorder};
    color: ${tokens.text.primary};
  }
`

export const InboundRequestsContainer = styled(Container)`
  margin-bottom: 1rem;
`

export const OutboundRequestsContainer = styled(Container)`
  margin-top: 1rem;
`

export const OutboundToggleHeader = styled(ContainerHeader)<{ $open: boolean }>`
  justify-content: space-between;
  cursor: pointer;
  border-bottom: ${({ $open }) => ($open ? `1px solid ${tokens.border.hairline}` : 'none')};
`

export const RequestList = styled.div`
  display: flex;
  flex-direction: column;
`

export const RequestRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem 0;
  & + & {
    border-top: 1px solid ${tokens.border.hairline};
  }
  &:first-child {
    padding-top: 0.25rem;
  }
  &:last-child {
    padding-bottom: 0.25rem;
  }
`

export const RequestTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 16rem;
`

export const RequestHead = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.5rem;
`

export const RequestCode = styled.span`
  font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
`

export const RequestTitleText = styled.p`
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${tokens.text.primary};
`

export const RequestMeta = styled.span`
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
`

export const RequestPillsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`

export const RequestActionsWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  margin-left: auto;
`

export const DeclineInlineWrap = styled.div`
  flex-basis: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${tokens.radius.control};
  border: 1px solid ${tokens.border.hairline};
  background: ${tokens.surface.inset};
`

export const DeclineFieldWrap = styled.div`
  flex: 1;
  min-width: 12rem;
`

export const OutboundList = styled.div`
  display: flex;
  flex-direction: column;
`

export const OutboundRow = styled.div<{ $muted: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  padding: 0.5rem 0;
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};
  & + & {
    border-top: 1px solid ${tokens.border.hairline};
  }
`

export const OutboundLine = styled.span`
  flex: 1;
  min-width: 16rem;
  font-size: ${tokens.font.label};
  line-height: 1.5;
  color: ${tokens.text.secondary};
`

export const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`

export const SummaryLabel = styled.span`
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
`
